import type { FC } from "react"
import React, { useMemo, useState } from "react"
import type { StackProps } from "@chakra-ui/react"
import {
  Tooltip,
  Stack,
  Tab as ChakraTab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Skeleton,
} from "@chakra-ui/react"
import capitalize from "lodash.capitalize"
import isEqual from "lodash.isequal"

import { isMultiChainOrganization } from "organization/helpers/organization"
import { shortenSupport } from "common/components/charts/helpers"
import type { Account, VoteStats, Vote } from "query/graphql"
import {
  VoteType,
  VotesSortBy,
  useProposalVoterVotesCastListQuery,
  useProposalVotesCastListQuery,
} from "query/graphql"
import VotesCastTab from "common/components/voting/VotesCastTab"
import EmptyEntity from "common/components/EmptyEntity"
import { getVoteStyles } from "common/helpers/voteStyle"
import { getWeightLabel, stringToBigNumber } from "common/helpers/bignumber"
import { useOrganization } from "organization/providers/OrganizationProvider"
import { useProposal } from "proposal/providers/ProposalProvider"
import { useAddress } from "web3/hooks/useAddress"

type VoteCastVote = Pick<Vote, "amount"> & {
  type: VoteType
  voter: Account
}

type Props = {
  proposalId: string
  choices?: string[] | null
  decimals?: number
  voteStats?: VoteStats[]
  viewAllVotesLink?: string
  displayChart?: boolean
  generateVoterLink: (address: string) => string
}

type GroupedVotesMap = { [k: string]: VoteCastVote[] }

const VotesCast: FC<Props & StackProps> = ({
  proposalId,
  choices,
  decimals,
  voteStats,
  viewAllVotesLink,
  generateVoterLink,
  displayChart = true,
  ...stackProps
}) => {
  const [selectedSupportIndex, setSelectedSupportIndex] = useState(0)
  const { organization } = useOrganization()
  const isMultiChain = organization
    ? isMultiChainOrganization(organization)
    : false

  const voterAddress = useAddress()

  const { isIncludePendingVotes } = useProposal()

  const defaultInput = {
    filters: {
      proposalId,
      includePendingVotes: true,
    },
    sort: {
      sortBy: VotesSortBy.Amount,
      isDescending: true,
    },
  }

  const { data: allVotesData, isLoading } = useProposalVotesCastListQuery({
    forInput: {
      ...defaultInput,
      ...{ filters: { ...defaultInput.filters, type: VoteType.For } },
    },
    againstInput: {
      ...defaultInput,
      ...{ filters: { ...defaultInput.filters, type: VoteType.Against } },
    },
    abstainInput: {
      ...defaultInput,
      ...{ filters: { ...defaultInput.filters, type: VoteType.Abstain } },
    },
  })

  const { data: accountVoteData } = useProposalVoterVotesCastListQuery(
    {
      input: {
        filters: {
          proposalId,
          voter: voterAddress,
          includePendingVotes: true,
        },
      },
    },
    { enabled: Boolean(voterAddress) },
  )

  const allVotes = ((allVotesData?.forVotes.nodes || []) as Vote[])
    .concat((allVotesData?.againstVotes.nodes || []) as Vote[])
    .concat((allVotesData?.abstainVotes.nodes || []) as Vote[])

  const accountVote =
    accountVoteData && accountVoteData.votes?.nodes
      ? (accountVoteData.votes.nodes[0] as Vote)
      : null

  const votes = useMemo(() => {
    if (!allVotes) return []

    // No account vote, then return all votes
    if (!accountVote) return allVotes

    // Move account vote to the top
    const otherVotes = allVotes.filter(
      (vote) => !isEqual(vote.voter.address, accountVote.voter.address),
    )

    return [accountVote, ...otherVotes]
  }, [accountVote, allVotes])

  const groupedVotes: GroupedVotesMap = useMemo(() => {
    if (!displayChart) return {}
    if (!votes) return {}

    return votes.reduce((result, vote) => {
      const prev = result[vote.type]

      return {
        ...result,
        [vote.type]: prev ? [...prev, vote] : [vote],
      }
    }, {} as GroupedVotesMap)
  }, [votes, displayChart])

  const sortedSupports = useMemo(() => {
    if (!displayChart) return []
    if (!choices?.length)
      return [VoteType.For, VoteType.Against, VoteType.Abstain]

    if (!groupedVotes) return choices.sort()

    return choices.sort((a: string, b: string) => {
      const aVoteStat = groupedVotes[a]
      const bVoteStat = groupedVotes[b]

      if (!aVoteStat && !bVoteStat) return 0

      if (!aVoteStat) {
        return 1
      }

      if (!bVoteStat) {
        return -1
      }

      return aVoteStat.length - bVoteStat.length
    })
  }, [choices, groupedVotes, displayChart])

  if (isLoading) {
    return (
      <Stack spacing={1} w="full" {...stackProps} py={4}>
        <Skeleton as="section" h="30px" mx={2} />
        <Skeleton as="section" h="30px" mx={2} />
        <Skeleton as="section" h="30px" mx={2} />
        <Skeleton as="section" h="30px" mx={2} />
      </Stack>
    )
  }

  if (!displayChart) {
    return null
  }

  const voteStyles = getVoteStyles(choices)
  const { colorScheme } = voteStyles[sortedSupports[selectedSupportIndex]]

  const handleTabChange = setSelectedSupportIndex

  // TODO(@nicolas): Refactor this since it is duplicated
  const getSupportVotesStats = (support: VoteType) => {
    const supportVotesStats = voteStats?.find((stat) => stat.type === support)

    if (isIncludePendingVotes) {
      if (support === VoteType.For) {
        const supportPendingVotesStats = voteStats?.find(
          (stat) => stat.type === VoteType.Pendingfor,
        )

        return {
          votersCount:
            (supportVotesStats?.votersCount ?? 0) +
            (supportPendingVotesStats?.votersCount ?? 0),
          votesCount: stringToBigNumber(
            supportVotesStats?.votesCount ?? "0",
          ).add(
            stringToBigNumber(
              supportPendingVotesStats?.votesCount ?? "0",
            ).toString(),
          ),
        }
      }

      if (support === VoteType.Against) {
        const supportPendingVotesStats = voteStats?.find(
          (stat) => stat.type === VoteType.Pendingagainst,
        )

        return {
          votersCount:
            (supportVotesStats?.votersCount ?? 0) +
            (supportPendingVotesStats?.votersCount ?? 0),
          votesCount: stringToBigNumber(
            supportVotesStats?.votesCount ?? "0",
          ).add(
            stringToBigNumber(
              supportPendingVotesStats?.votesCount ?? "0",
            ).toString(),
          ),
        }
      }

      if (support === VoteType.Abstain) {
        const supportPendingVotesStats = voteStats?.find(
          (stat) => stat.type === VoteType.Pendingabstain,
        )

        return {
          votersCount:
            (supportVotesStats?.votersCount ?? 0) +
            (supportPendingVotesStats?.votersCount ?? 0),
          votesCount: stringToBigNumber(
            supportVotesStats?.votesCount ?? "0",
          ).add(
            stringToBigNumber(
              supportPendingVotesStats?.votesCount ?? "0",
            ).toString(),
          ),
        }
      }
    }

    return {
      votersCount: supportVotesStats?.votersCount ?? 0,
      votesCount: supportVotesStats?.votesCount ?? "0",
    }
  }

  return (
    <Stack as="section" spacing={2} w="full" {...stackProps}>
      <Tabs colorScheme={colorScheme} onChange={handleTabChange}>
        <TabList>
          {sortedSupports.map((support) => {
            const label = capitalize(support)

            return (
              <Tooltip key={`tab_list_ballot_type_${support}`} label={label}>
                <ChakraTab flex={1}>{shortenSupport(label)}</ChakraTab>
              </Tooltip>
            )
          })}
        </TabList>
        <TabPanels>
          {sortedSupports.map((support) => {
            const votes = groupedVotes?.[support]

            const { votersCount, votesCount } = getSupportVotesStats(
              support as VoteType,
            )

            const addressesCount = Number(votersCount) ?? 0
            const totalWeightLabel = votesCount
              ? getWeightLabel(`${votesCount}`, decimals || 0)
              : "0"

            const exactTotalWeightLabel = votesCount
              ? getWeightLabel(`${votesCount}`, decimals || 0, false)
              : "0"

            return (
              <TabPanel key={`tab_panel_ballot_type_${support}`} p={0}>
                {votes?.length ? (
                  <VotesCastTab
                    addressesCount={addressesCount}
                    decimals={decimals}
                    displayChainLogo={isMultiChain}
                    exactTotalWeightLabel={exactTotalWeightLabel}
                    generateVoterLink={generateVoterLink}
                    totalWeightLabel={totalWeightLabel}
                    viewAllVotesLink={viewAllVotesLink}
                    voterAddress={voterAddress}
                    votes={votes as Vote[]}
                  />
                ) : (
                  <EmptyEntity
                    description="Votes will appear here"
                    title="No votes yet"
                  />
                )}
              </TabPanel>
            )
          })}
        </TabPanels>
      </Tabs>
    </Stack>
  )
}

export default VotesCast
